import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CancelIcon from '@mui/icons-material/Cancel';
import MessageIcon from '@mui/icons-material/Message';
import PetsIcon from '@mui/icons-material/Pets';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Rating,
  Typography,
} from '@mui/material';
import { ClientBookingWithDetails } from 'happypaws-shared/src/Booking/models/BookingWithDetails';
import { ServiceType, SERVICE_TYPE_LABELS } from 'happypaws-shared/src/Services/services';
import React from 'react';
import { BookingStatus } from '../../../../happypaws-shared/src/Booking/models/Booking';

interface ClientBookingCardProps {
  booking: ClientBookingWithDetails;
  onViewDetails?: (id: string) => void;
  onMessageSitter?: (id: string) => void;
  onCancelBooking?: (id: string) => void;
  onLeaveReview?: (id: string) => void;
  onBookAgain?: (id: string) => void;
}

const getStatusConfig = (status: BookingStatus) => {
  switch (status) {
    case BookingStatus.PENDING:
      return { label: 'Pending', color: '#ED6C02', bgColor: '#FFF3E0' };
    case BookingStatus.CONFIRMED:
      return { label: 'Confirmed', color: '#1976D2', bgColor: '#E3F2FD' };
    case BookingStatus.COMPLETED:
      return { label: 'Completed', color: '#66BB6A', bgColor: '#E8F5E9' };
    case BookingStatus.CANCELLED:
      return { label: 'Cancelled', color: '#9E9E9E', bgColor: '#F5F5F5' };
    default:
      return { label: 'Unknown', color: '#9E9E9E', bgColor: '#F5F5F5' };
  }
};

const formatServiceName = (serviceType: string) =>
  SERVICE_TYPE_LABELS[serviceType as ServiceType] ?? serviceType;

const ClientBookingCard: React.FC<ClientBookingCardProps> = ({
  booking,
  onViewDetails,
  onMessageSitter,
  onCancelBooking,
  onLeaveReview,
  onBookAgain,
}) => {
  const statusConfig = getStatusConfig(booking.status as BookingStatus);
  const sitterName = `${booking.sitterFirstName} ${booking.sitterLastName}`;

  const renderActions = () => {
    switch (booking.status) {
      case BookingStatus.PENDING:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails?.(booking.id)}
            >
              View Details
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<MessageIcon />}
              onClick={() => onMessageSitter?.(booking.id)}
            >
              Message Sitter
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => onCancelBooking?.(booking.id)}
            >
              Cancel Booking
            </Button>
          </Box>
        );
      case BookingStatus.CONFIRMED:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails?.(booking.id)}
            >
              View Details
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<MessageIcon />}
              onClick={() => onMessageSitter?.(booking.id)}
            >
              Message Sitter
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => onCancelBooking?.(booking.id)}
            >
              Cancel Booking
            </Button>
          </Box>
        );
      case BookingStatus.COMPLETED:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails?.(booking.id)}
            >
              View Details
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<RateReviewIcon />}
              onClick={() => onLeaveReview?.(booking.id)}
            >
              Leave Review
            </Button>
          </Box>
        );
      case BookingStatus.CANCELLED:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails?.(booking.id)}
            >
              View Details
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<ReplayIcon />}
              onClick={() => onBookAgain?.(booking.id)}
            >
              Book Again
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: '4px solid',
        borderLeftColor: statusConfig.color,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
      }}
    >
      <CardContent>
        {/* Top Row: Date Range + Status */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body1" fontWeight={600}>
              {booking.startDate} — {booking.endDate}
            </Typography>
          </Box>
          <Chip
            label={statusConfig.label}
            sx={{
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
              fontWeight: 600,
              border: `1px solid ${statusConfig.color}`,
            }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Middle Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2,
          }}
        >
          {/* Sitter Info */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Avatar
              src={booking.sitterPhotoUrl ?? undefined}
              sx={{
                width: 52,
                height: 52,
                backgroundColor: '#178B8E',
                fontSize: '1.3rem',
                fontWeight: 600,
              }}
            >
              {booking.sitterFirstName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600}>
                {sitterName}
              </Typography>
              <Rating value={0} size="small" readOnly />
            </Box>
          </Box>

          {/* Pets */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PetsIcon sx={{ fontSize: 20, color: '#178B8E' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Pet{booking.pets.length > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {booking.pets.map((p) => p.petName).join(', ')}
              </Typography>
            </Box>
          </Box>

          {/* Service */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              Service
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatServiceName(booking.serviceType)}
            </Typography>
          </Box>

          {/* Price */}
          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
              ${booking.totalPrice}
            </Typography>
          </Box>
        </Box>

        {renderActions()}
      </CardContent>
    </Card>
  );
};

export default ClientBookingCard;
